import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/corrosive-waste-disposal-page.module.css'
// eslint-disable-next-line import/extensions
import corrosiveWaste from '../../json/corrosive-waste-disposal-data.json'

const corrosiveServiceData = {
  name: 'Corrosive Waste Disposal',
  description:
    'Compliant corrosive waste disposal for businesses. We handle waste acids, caustics, plating and etching baths, battery acid, and other corrosive liquids as DOT Class 8 / RCRA D002 corrosive waste, with full EPA documentation.',
  serviceType: 'Corrosive Waste Disposal',
  url: 'https://getdimension.com/corrosive-waste-disposal',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Waste Acid Disposal' },
    { name: 'Caustic and Base Disposal' },
    { name: 'Plating, Etching and Pickling Bath Disposal' },
    { name: 'Battery and Electrolyte Acid Disposal' },
    { name: 'Industrial Cleaner and Descaler Disposal' },
  ],
}

const corrosiveFAQData = {
  questions: corrosiveWaste.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function CorrosiveWasteDisposal() {
  return (
    <>
      <Head>
        <title>Corrosive Waste Disposal for Businesses — Acids & Caustics | Dimension</title>
        <meta
          name="description"
          content="Compliant corrosive waste disposal for businesses — waste acids, caustics, plating baths, battery acid. DOT Class 8 & RCRA D002, instant quote, 30% less than competitor rates."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/corrosive-waste-disposal" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={corrosiveServiceData} />
        <SchemaMarkup type="FAQPage" data={corrosiveFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heading={corrosiveWaste.pitch.heading}
          material={corrosiveWaste.pitch.material}
          pitchText={corrosiveWaste.pitch.pitchText}
          ctaLink={corrosiveWaste.pitch.ctaLink}
          ctaText={corrosiveWaste.pitch.ctaText}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureList {...corrosiveWaste.solutions} />
        <SustainabilityDashboardPromo />
        <FeatureList {...corrosiveWaste.benefits} />
        <CaseStudy {...corrosiveWaste.caseStudy} />
        <Approach {...corrosiveWaste.approach} />
        <FAQ {...corrosiveWaste.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Hazardous Waste Disposal',
              href: '/hazardous-waste-disposal',
              description:
                'Safe disposal of chemical waste, solvents, and other hazardous materials for businesses.',
            },
            {
              label: 'Regulated Waste Disposal',
              href: '/regulated-waste-disposal',
              description:
                'The catch-all for EPA- and DOT-regulated materials — universal waste, batteries, aerosols, gases, and more.',
            },
            {
              label: 'Flammable Waste Disposal',
              href: '/flammable-waste-disposal',
              description:
                'Compliant disposal of solvents, used fuel, aerosols, and other DOT Class 3 ignitable waste.',
            },
            {
              label: 'Lab Pack Services',
              href: '/lab-pack-services',
              description:
                'Chemist-led identification, segregation, and pack-out of small-container laboratory chemicals.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default CorrosiveWasteDisposal
